// src/utils/initMap.js
let map;
let marker;
let autocomplete;
let geocoder;
let placeSearch;

export const initMap = (updateMapData) => {
  // Clean up any existing map instances first
  if (map) {
    if (marker) marker.setMap(null);
    const mapElement = document.getElementById('map');
    if (mapElement) mapElement.innerHTML = '';
  }

  // Check if required DOM elements exist
  const mapElement = document.getElementById('map');
  const pacInput = document.getElementById('pac-input');
  const addressesList = document.getElementById('addresses');
  
  if (!mapElement) {
    console.error('Map element not found');
    return () => {};
  }

  // Initialize map with default location (fallback)
  const defaultLocation = { lat: 28.6542, lng: 77.4038 };
  map = new google.maps.Map(mapElement, {
    center: defaultLocation,
    zoom: 13,
    mapTypeControl: false,
  });

  geocoder = new google.maps.Geocoder();

  // Initialize marker
  marker = new google.maps.Marker({
    map: map,
    draggable: true,
  });

  // Initialize autocomplete if input exists
  if (pacInput) {
    autocomplete = new google.maps.places.Autocomplete(pacInput, {
      types: ['geocode'],
    });
    autocomplete.bindTo('bounds', map);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) {
        console.log("No details available for input: '" + place.name + "'");
        return;
      }
      if (place.geometry.viewport) map.fitBounds(place.geometry.viewport);
      else {
        map.setCenter(place.geometry.location);
        map.setZoom(17);
      }
      marker.setPosition(place.geometry.location);
      updateAddressList(place);
    });
  }

  // Add map click listener
  map.addListener('click', (event) => {
    marker.setPosition(event.latLng);
    geocodePosition(event.latLng);
  });

  // Add marker drag end listener
  marker.addListener('dragend', () => {
    geocodePosition(marker.getPosition());
  });

  // Function: Reverse Geocode from LatLng
  const geocodePosition = (pos) => {
    geocoder.geocode({ location: pos }, (results, status) => {
      if (status === 'OK' && results[0]) {
        updateAddressList(results[0]);
      } else {
        console.error('Geocoder failed due to: ' + status);
      }
    });
  };

  // Function: Extract & update address info
  const updateAddressList = (place) => {
    if (!place) return;

    const addressComponents = {
      street_number: '',
      route: '',
      locality: '',
      administrative_area_level_2: '',
      administrative_area_level_1: '',
      country: '',
      postal_code: '',
    };

    if (place.address_components) {
      place.address_components.forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) addressComponents.street_number = component.long_name;
        else if (types.includes('route')) addressComponents.route = component.long_name;
        else if (types.includes('locality')) addressComponents.locality = component.long_name;
        else if (types.includes('administrative_area_level_2')) addressComponents.administrative_area_level_2 = component.long_name;
        else if (types.includes('administrative_area_level_1')) addressComponents.administrative_area_level_1 = component.long_name;
        else if (types.includes('country')) addressComponents.country = component.long_name;
        else if (types.includes('postal_code')) addressComponents.postal_code = component.long_name;
      });
    }

    const houseNumberAndStreet = `${addressComponents.street_number} ${addressComponents.route}`.trim();
    const locality = addressComponents.locality || addressComponents.administrative_area_level_2;
    const country = addressComponents.country;
    const zipcode = addressComponents.postal_code;
    const fullAddress = place.formatted_address;

    try {
      updateMapData(
        fullAddress,
        place.geometry.location.lat(),
        place.geometry.location.lng(),
        zipcode,
        houseNumberAndStreet,
        locality,
        country
      );

      // Update form fields (if exist)
      const houseNumberInput = document.getElementById('house-number-street');
      const localityInput = document.getElementById('locality');
      const zipcodeInput = document.getElementById('zipcode');

      if (houseNumberInput) houseNumberInput.value = houseNumberAndStreet;
      if (localityInput) localityInput.value = locality;
      if (zipcodeInput) zipcodeInput.value = zipcode;

      if (addressesList) addressesList.innerHTML = `<li>${fullAddress}</li>`;
    } catch (error) {
      console.error('Error updating address fields:', error);
    }
  };

  // ✅ Load user’s current location on initial map load
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const currentPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        map.setCenter(currentPos);
        map.setZoom(15);
        marker.setPosition(currentPos);
        geocodePosition(currentPos); // Reverse geocode to get address
      },
      (error) => {
        console.warn('Geolocation permission denied or unavailable:', error.message);
        // Fallback: Use default location
        map.setCenter(defaultLocation);
        marker.setPosition(defaultLocation);
        geocodePosition(defaultLocation);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  } else {
    console.error('Geolocation is not supported by this browser.');
    map.setCenter(defaultLocation);
    marker.setPosition(defaultLocation);
    geocodePosition(defaultLocation);
  }

  // Cleanup function
  return () => {
    if (marker) marker.setMap(null);
    if (autocomplete) google.maps.event.clearInstanceListeners(autocomplete);
    if (map) google.maps.event.clearInstanceListeners(map);
  };
};

// Cleanup for unmount
export const cleanupMap = () => {
  if (marker) {
    marker.setMap(null);
    marker = null;
  }
  if (autocomplete) {
    google.maps.event.clearInstanceListeners(autocomplete);
    autocomplete = null;
  }
  if (map) {
    google.maps.event.clearInstanceListeners(map);
    map = null;
  }
  geocoder = null;
  placeSearch = null;
};
